import React, { useEffect, useState } from "react";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import {
  AccessRole,
  listAccessRoles,
} from "../../services/accessRolesService";

const EDGE_API_BASE =
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com";

type ResolvedEdgeCamera = {
  edgeGatewayId: string;
  edgeCameraId: string;
  name: string;
  localHost: string;
  httpPort: number;
  rtspPort: number;
  mac?: string;
  present: boolean;
  lastSeenAt?: string | null;
  gatewayName: string;
  gatewayStatus: string;
  gatewayLastSeenAt?: string | null;
  gatewayClaimed: boolean;
};

type ResolveResponse = {
  camera: ResolvedEdgeCamera;
  alreadyClaimed: boolean;
  claimedCameraId?: string | null;
  gatewayWillBeClaimed?: boolean;
};

type Props = {
  onCameraCreated?: (cameraId: string) => void | Promise<void>;
};

async function idToken() {
  const tokens = await cognitoUserPoolsTokenProvider.getTokens();
  const token = tokens?.idToken?.toString();
  if (!token) {
    throw new Error("No ID token found. Please sign out and sign in again.");
  }
  return token;
}

async function readResponse(response: Response) {
  const text = await response.text();
  try {
    return { text, json: JSON.parse(text) };
  } catch {
    return { text, json: null };
  }
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Unexpected Gateway camera error.";
}

function cleanCode(value: string) {
  return value.trim().toUpperCase();
}

export default function GatewayCameraOnboardingForm({
  onCameraCreated,
}: Props) {
  const [registrationCode, setRegistrationCode] = useState("");
  const [resolved, setResolved] = useState<ResolveResponse | null>(null);
  const [cameraName, setCameraName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [archiveHours, setArchiveHours] = useState(48);
  const [accessRoles, setAccessRoles] = useState<AccessRole[]>([]);
  const [portalRole, setPortalRole] = useState<
    "OWNER" | "INTEGRATOR" | "ADMIN" | "VIEWER" | null
  >(null);
  const [selectedAccessRoleId, setSelectedAccessRoleId] = useState("");
  const [accessRoleError, setAccessRoleError] = useState("");
  const [resolving, setResolving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState("");
  const [statusIsError, setStatusIsError] = useState(false);

  const code = cleanCode(registrationCode);

  useEffect(() => {
    let active = true;
    void listAccessRoles()
      .then((result) => {
        if (!active) return;
        const items = Array.isArray(result.items) ? result.items : [];
        setPortalRole(result.callerRole);
        setAccessRoles(items);
        setSelectedAccessRoleId((current) =>
          current && items.some((role) => role.accessRoleId === current)
            ? current
            : items[0]?.accessRoleId || "",
        );
        setAccessRoleError("");
      })
      .catch((error) => {
        if (!active) return;
        setAccessRoles([]);
        setAccessRoleError(
          `Unable to load camera Access Roles: ${errorMessage(error)}`,
        );
      });
    return () => {
      active = false;
    };
  }, []);

  async function resolveRegistrationCode() {
    setStatus("");
    setStatusIsError(false);
    setResolved(null);
    if (!code) {
      setStatusIsError(true);
      setStatus("Enter the camera registration code shown in Gateway OS.");
      return;
    }

    setResolving(true);
    try {
      const token = await idToken();
      const response = await fetch(`${EDGE_API_BASE}/edge-cameras/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ registrationCode: code }),
      });
      const { text, json } = await readResponse(response);
      if (!response.ok) {
        throw new Error(
          json?.message || text || `Unable to resolve camera (${response.status}).`,
        );
      }
      const result = json as ResolveResponse;
      if (!result?.camera?.edgeGatewayId || !result?.camera?.edgeCameraId) {
        throw new Error("The Gateway returned incomplete camera inventory data.");
      }
      setResolved(result);
      setCameraName(result.camera.name || "Gateway Camera");
      if (result.alreadyClaimed) {
        setStatusIsError(true);
        setStatus(
          result.claimedCameraId
            ? `This registration code is already assigned to camera ${result.claimedCameraId}.`
            : "This registration code has already been used.",
        );
      } else {
        setStatus(
          result.gatewayWillBeClaimed
            ? "Camera found. Adding it will also link this physical Gateway to your account."
            : "Camera found through the registered Gateway.",
        );
      }
    } catch (error) {
      setStatusIsError(true);
      setStatus(`Find Camera failed: ${errorMessage(error)}`);
    } finally {
      setResolving(false);
    }
  }

  async function createGatewayCamera() {
    setStatus("");
    setStatusIsError(false);
    if (!resolved || resolved.alreadyClaimed) {
      setStatusIsError(true);
      setStatus("Find an unused Gateway camera registration code first.");
      return;
    }
    if (!cameraName.trim() || !username.trim() || !password) {
      setStatusIsError(true);
      setStatus("Camera name, ONVIF username, and password are required.");
      return;
    }
    if (!Number.isInteger(archiveHours) || archiveHours < 0) {
      setStatusIsError(true);
      setStatus("Archive hours must be zero or greater.");
      return;
    }
    if (portalRole === "ADMIN" && !selectedAccessRoleId) {
      setStatusIsError(true);
      setStatus("Choose one of the Access Roles assigned by your Integrator.");
      return;
    }

    setCreating(true);
    try {
      const token = await idToken();
      const response = await fetch(`${EDGE_API_BASE}/edge-cameras/onboard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          registrationCode: code,
          name: cameraName.trim(),
          archiveHours,
          ...(selectedAccessRoleId
            ? { accessRoleId: selectedAccessRoleId }
            : {}),
          credentials: {
            username: username.trim(),
            password,
          },
        }),
      });
      const { text, json } = await readResponse(response);
      if (!response.ok) {
        throw new Error(
          json?.message || text || `Unable to add Gateway camera (${response.status}).`,
        );
      }

      const cameraId = String(json?.cameraId || "");
      setPassword("");
      setResolved((current) =>
        current
          ? { ...current, alreadyClaimed: true, claimedCameraId: cameraId }
          : current,
      );
      setStatus(
        `Camera ${cameraId} was added to Qumra. Its credentials are stored securely. It will remain disabled until secure tunnel and ONVIF profile validation are completed.`,
      );
      await onCameraCreated?.(cameraId);
    } catch (error) {
      setStatusIsError(true);
      setStatus(`Add Gateway Camera failed: ${errorMessage(error)}`);
    } finally {
      setCreating(false);
    }
  }

  const busy = resolving || creating;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div
        style={{
          border: "1px solid #dbe4ef",
          borderRadius: 12,
          padding: 16,
          background: "#f8fafc",
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Gateway camera</div>
        <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
          Enter the camera registration code from Gateway OS. Camera credentials
          stay in the cloud and are not stored on the physical Gateway.
        </div>
      </div>

      <label style={{ display: "grid", gap: 7 }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>
          Camera registration code *
        </span>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10 }}>
          <input
            value={registrationCode}
            onChange={(event) => {
              setRegistrationCode(event.target.value.toUpperCase());
              setResolved(null);
              setStatus("");
            }}
            placeholder="CAM-XXXX-XXXX"
            autoCapitalize="characters"
            autoComplete="off"
            disabled={busy}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              font: "inherit",
            }}
          />
          <button
            type="button"
            onClick={() => void resolveRegistrationCode()}
            disabled={busy || !code}
            style={{
              border: "1px solid #94a3b8",
              borderRadius: 8,
              padding: "0 16px",
              background: "white",
              fontWeight: 700,
              cursor: busy || !code ? "not-allowed" : "pointer",
              opacity: busy || !code ? 0.6 : 1,
            }}
          >
            {resolving ? "Finding..." : "Find Camera"}
          </button>
        </div>
      </label>

      {resolved?.camera && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 10,
            border: "1px solid #bfdbfe",
            borderRadius: 12,
            padding: 14,
            background: "#eff6ff",
          }}
        >
          <GatewayDetail label="Gateway" value={resolved.camera.gatewayName} />
          <GatewayDetail
            label="Gateway status"
            value={resolved.camera.gatewayStatus}
          />
          <GatewayDetail label="Local camera" value={resolved.camera.localHost} />
          <GatewayDetail
            label="ONVIF / HTTP"
            value={String(resolved.camera.httpPort)}
          />
          <GatewayDetail label="RTSP" value={String(resolved.camera.rtspPort)} />
          <GatewayDetail
            label="Gateway account"
            value={resolved.camera.gatewayClaimed ? "Linked" : "Will link on add"}
          />
        </div>
      )}

      {resolved && !resolved.alreadyClaimed && (
        <>
          {(portalRole === "INTEGRATOR" || portalRole === "ADMIN") && (
            <GatewayField
              label={portalRole === "ADMIN" ? "Access Role *" : "Access Role"}
            >
              <select
                value={selectedAccessRoleId}
                onChange={(event) =>
                  setSelectedAccessRoleId(event.target.value)
                }
                disabled={busy}
                style={inputStyle}
              >
                {portalRole === "INTEGRATOR" && (
                  <option value="">Unassigned for now</option>
                )}
                {accessRoles.length === 0 && portalRole === "ADMIN" ? (
                  <option value="">No Access Roles assigned</option>
                ) : (
                  accessRoles.map((accessRole) => (
                    <option
                      key={accessRole.accessRoleId}
                      value={accessRole.accessRoleId}
                    >
                      {accessRole.name}
                    </option>
                  ))
                )}
              </select>
              <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
                {portalRole === "ADMIN"
                  ? "The camera will be visible to you and will roll up to your parent Integrator."
                  : "Optional. The camera remains visible to your Integrator account even when no Access Role is selected."}
              </div>
            </GatewayField>
          )}

          {accessRoleError && (
            <div style={{ color: "#b91c1c", fontSize: 13 }}>
              {accessRoleError}
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            <GatewayField label="Camera name *">
              <input
                value={cameraName}
                onChange={(event) => setCameraName(event.target.value)}
                disabled={busy}
                style={inputStyle}
              />
            </GatewayField>
            <GatewayField label="Archive hours">
              <input
                type="number"
                min={0}
                value={archiveHours}
                onChange={(event) => setArchiveHours(Number(event.target.value))}
                disabled={busy}
                style={inputStyle}
              />
            </GatewayField>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            <GatewayField label="ONVIF username *">
              <input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                disabled={busy}
                style={inputStyle}
              />
            </GatewayField>
            <GatewayField label="ONVIF password *">
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                disabled={busy}
                style={inputStyle}
              />
            </GatewayField>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => void createGatewayCamera()}
              disabled={busy}
              style={{
                border: 0,
                borderRadius: 8,
                padding: "10px 18px",
                background: "#2563eb",
                color: "white",
                fontWeight: 800,
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.65 : 1,
              }}
            >
              {creating ? "Adding..." : "Add Gateway Camera"}
            </button>
          </div>
        </>
      )}

      {status && (
        <div
          role={statusIsError ? "alert" : "status"}
          style={{
            borderRadius: 10,
            padding: "11px 13px",
            background: statusIsError ? "#fef2f2" : "#f0fdf4",
            border: `1px solid ${statusIsError ? "#fecaca" : "#bbf7d0"}`,
            color: statusIsError ? "#991b1b" : "#166534",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {status}
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  font: "inherit",
  background: "white",
};

function GatewayField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: 7 }}>
      <span style={{ fontWeight: 700, fontSize: 13 }}>{label}</span>
      {children}
    </label>
  );
}

function GatewayDetail({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 3 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          color: "#0f172a",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={value}
      >
        {value || "—"}
      </div>
    </div>
  );
}
